import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { UtilityService } from 'src/app/services/utility.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-auto-sso',
  templateUrl: './auto-sso.component.html',
  styleUrls: ['./auto-sso.component.scss'],
  standalone: false
})
export class AutoSsoComponent implements OnInit {
  error: string | null = null;
  logoUrl: string = '';
  brandName: string = '';

  // TESTING: Set to true to simulate error and test fallback UI
  private TEST_ERROR_MODE = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private api: ApiService,
    private utility: UtilityService,
    private themeService: ThemeService
  ) {}

  ngOnInit(): void {
    console.log('[AutoSSO] Component initialized - starting automatic SSO flow');

    // Get logo and brand name from theme service
    this.logoUrl = this.themeService.getLogoPath();
    this.brandName = this.themeService.getDisplayName();

    // Check for any error from query params (from auth callback)
    this.route.queryParams.subscribe(params => {
      const errorParam = params['error'];

      // If there's an error parameter, show fallback screen
      if (errorParam) {
        console.log('[AutoSSO] Authentication error detected:', errorParam, '- showing fallback screen');

        // Map error codes to user-friendly messages
        let errorMessage = 'Please sign in again.';
        switch (errorParam) {
          case 'INVALID_SESSION':
            errorMessage = 'Your session has expired or is invalid.';
            break;
          case 'API_ERROR':
            errorMessage = 'Authentication service error.';
            break;
          case 'MISSING_PARAMS':
            errorMessage = 'Authentication request incomplete.';
            break;
          case 'LOGIN_FAILED':
            errorMessage = 'Login failed. Please try again.';
            break;
          case 'MISSING_EMPLOYEE_NUMBER':
            errorMessage = 'Your Microsoft profile is missing an employee number. Please contact your administrator.';
            break;
          case 'ACCOUNT_INACTIVE':
            errorMessage = 'Your account is not active. Please contact an administrator.';
            break;
          case 'ACCOUNT_DELETED':
            errorMessage = 'Your account has been deactivated. Please contact an administrator.';
            break;
          case 'JOB_TITLE_NOT_RECOGNIZED':
            errorMessage = 'Your job title is not authorized for this system. Please contact your administrator.';
            break;
          case 'MISSING_HOSPITAL_INFO':
            errorMessage = 'Your Microsoft profile is missing hospital information. Please contact your administrator.';
            break;
          case 'SAML_VALIDATION_FAILED':
            errorMessage = 'Single Sign-On validation failed. Please try again.';
            break;
          default:
            errorMessage = 'Authentication failed. Please try again.';
        }

        this.handleError(errorMessage);
        return;
      }

      // TESTING: Simulate error for fallback UI testing
      if (this.TEST_ERROR_MODE) {
        console.log('[AutoSSO] TEST MODE: Simulating error');
        this.handleError('Unable to connect to authentication service');
        return;
      }

      // Automatically initiate Microsoft SSO
      this.startMicrosoftSSO();
    });
  }

  startMicrosoftSSO(): void {
    console.log('[AutoSSO] Initiating SAML SSO - redirecting to IdP...');
    this.error = null;

    // SAML SP-initiated: direct browser redirect to backend login endpoint
    // passport-saml will generate AuthnRequest and redirect to IdP
    const serverUrl = this.api.serverUrl;
    window.location.href = `${serverUrl}/api/auth/saml/login`;
  }

  handleError(message: string): void {
    this.error = message;
    console.error('[AutoSSO] Error:', message);
  }

  // Manual fallback - user can click to try again
  manualSignIn(): void {
    console.log('[AutoSSO] Manual sign-in triggered');
    this.startMicrosoftSSO();
  }

  // Navigate to regular login page as fallback
  goToRegularLogin(): void {
    console.log('[AutoSSO] Navigating to regular login page');
    this.router.navigate(['/auth/pcv-signin']);
  }
}
